/**
 * Shared Verify sub-agent inline status chrome — starting, cancelled, and
 * error placeholders for `VerifyChildRunInline`.
 */

import { TypographyCaption } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { AlertTriangle, Ban, Loader2 } from 'lucide-react';
import { useIntl } from 'react-intl';

import { projectRunFailureMessages } from '../../../../data/projects/components/projectRunFailure.messages';
import type { BlockStatus } from '../../../../data/projects/timeline/presentation/types';

export const QuestionCaption = ({ question }: { question: string }): JSX.Element => (
  <p className="break-words text-sm leading-snug text-text-secondary [overflow-wrap:anywhere]">
    {question}
  </p>
);

export const ErrorNotice = ({
  message,
  className,
}: {
  message?: string;
  className?: string;
}): JSX.Element => {
  const intl = useIntl();
  const displayMessage =
    message ?? intl.formatMessage(projectRunFailureMessages.infraToolError);

  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-md border border-validation-destructive-muted bg-validation-destructive-muted px-2.5 py-1.5 text-xs text-validation-destructive',
        className
      )}
    >
      <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
      <span className="break-words">{displayMessage}</span>
    </div>
  );
};

export const CancelledNotice = ({
  question,
  className,
}: {
  question?: string;
  className?: string;
}): JSX.Element => (
  <div
    className={cn(
      'flex flex-col gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs text-text-secondary',
      className
    )}
  >
    <div className="flex items-center gap-2">
      <Ban className="size-3.5 shrink-0 text-text-tertiary" />
      <TypographyCaption className="text-text-secondary">
        Note verification cancelled.
      </TypographyCaption>
    </div>
    {question && <QuestionCaption question={question} />}
  </div>
);

export interface StartingPlaceholderProps {
  question: string | undefined;
  status: BlockStatus;
  errorMessage?: string;
  isLoading?: boolean;
  className?: string;
}

export const StartingPlaceholder = ({
  question,
  status,
  errorMessage,
  isLoading = true,
  className,
}: StartingPlaceholderProps): JSX.Element => {
  if (status === 'error') {
    return <ErrorNotice message={errorMessage} className={className} />;
  }

  if (status === 'cancelled') {
    return <CancelledNotice question={question} className={className} />;
  }

  if (status === 'success') {
    return (
      <div className={cn('flex flex-col gap-1.5', className)}>
        <TypographyCaption className="text-text-tertiary">
          Verification completed with no streamed output.
        </TypographyCaption>
        {question && <QuestionCaption question={question} />}
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="flex items-center gap-2 py-1">
        <Loader2
          className={cn('size-3.5 text-text-tertiary', isLoading && 'animate-spin')}
        />
        <TypographyCaption className="text-text-tertiary">
          Tracing note provenance…
        </TypographyCaption>
      </div>
      {question && <QuestionCaption question={question} />}
    </div>
  );
};
